import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { driverPublicAPI, driversAPI } from "../../services/api";
import {
  PublicShipmentForDrivers,
  DriverAttachment,
  ServiceSnapshot,
} from "../../types";
import { ShipmentGroupForDrivers } from "../../types";
import { enqueueAction } from "../../utils/offlineQueue";

interface DriverState {
  shipments: PublicShipmentForDrivers[];
  shipment: PublicShipmentForDrivers | null;
  groups: ShipmentGroupForDrivers[];
  currentGroup: ShipmentGroupForDrivers | null;
  loading: boolean;
  error: string | null;
  attachmentLoading: boolean;
  attachmentError: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  // Offline support flags
  isFromCache: boolean;
  cachedAt: number | null;
}

const initialState: DriverState = {
  shipments: [],
  shipment: null,
  groups: [],
  currentGroup: null,
  loading: false,
  error: null,
  attachmentLoading: false,
  attachmentError: null,
  pagination: {
    page: 1,
    limit: 1000,
    total: 0,
    pages: 0,
  },
  isFromCache: false,
  cachedAt: null,
};

const getDriverAPI = (isProtected: boolean = false) => {
  return isProtected ? driversAPI : driverPublicAPI;
};

/**
 * Fetch shipments with offline caching
 */
export const fetchShipments = createAsyncThunk(
  "driver/fetchShipments",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await driversAPI.getShipments(params);
      const data = response.data.data;

      // Cache shipments for offline access
      const { cacheShipments } = await import("../../utils/indexedDB");
      await cacheShipments(data.shipments).catch((err) => {
        console.warn("[Offline] Failed to cache shipments:", err);
      });

      return data;
    } catch (error: any) {
      // If offline, try to get from cache
      if (!navigator.onLine || error.code === "ERR_NETWORK") {
        // console.log("[Offline] Network failed, trying cache...");

        try {
          const { getAllCachedShipments } =
            await import("../../utils/indexedDB");
          const cachedShipments = await getAllCachedShipments();

          if (cachedShipments.length > 0) {
            // console.log(
            //   "[Offline] Serving shipments from cache:",
            //   cachedShipments.length,
            // );
            return {
              shipments: cachedShipments,
              pagination: {
                page: 1,
                limit: cachedShipments.length,
                total: cachedShipments.length,
                pages: 1,
              },
            };
          }
        } catch (cacheErr) {
          console.error("[Offline] Cache retrieval failed:", cacheErr);
        }
      }

      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch shipments",
      );
    }
  },
);

/**
 * Fetch driver groups with offline caching
 */
export const fetchDriverGroups = createAsyncThunk(
  "driver/fetchDriverGroups",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await driversAPI.getGroups(params);
      const data = response.data.data;

      // Cache groups for offline access
      const { cacheGroups } = await import("../../utils/indexedDB");
      await cacheGroups(data.groups).catch((err) => {
        console.warn("[Offline] Failed to cache groups:", err);
      });

      return data;
    } catch (error: any) {
      // If offline, try to get from cache
      if (!navigator.onLine || error.code === "ERR_NETWORK") {
        // console.log("[Offline] Network failed, trying cache...");

        try {
          const { getAllCachedGroups } = await import("../../utils/indexedDB");
          const cachedGroups = await getAllCachedGroups();

          if (cachedGroups.length > 0) {
            // console.log(
            //   "[Offline] Serving groups from cache:",
            //   cachedGroups.length,
            // );
            return {
              groups: cachedGroups,
            };
          }
        } catch (cacheErr) {
          console.error("[Offline] Cache retrieval failed:", cacheErr);
        }
      }

      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch groups",
      );
    }
  },
);

/**
 * Fetch specific group details with offline caching
 */
export const fetchDriverGroupById = createAsyncThunk(
  "driver/fetchDriverGroupById",
  async (groupId: string, { rejectWithValue }) => {
    try {
      const response = await driversAPI.getGroupById(groupId);
      const group = response.data.data.group;

      // Cache group for offline access
      const { cacheGroup } = await import("../../utils/indexedDB");
      await cacheGroup(group).catch((err) => {
        console.warn("[Offline] Failed to cache group:", err);
      });

      return group;
    } catch (error: any) {
      // If offline, try to get from cache
      if (!navigator.onLine || error.code === "ERR_NETWORK") {
        // console.log("[Offline] Network failed, trying cache...");

        try {
          const { getCachedGroup } = await import("../../utils/indexedDB");
          const cachedGroup = await getCachedGroup(groupId);

          if (cachedGroup) {
            // console.log(
            //   "[Offline] Serving group from cache:",
            //   cachedGroup.groupNumber,
            // );
            return cachedGroup;
          }
        } catch (cacheErr) {
          console.error("[Offline] Cache retrieval failed:", cacheErr);
        }
      }

      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch group details",
      );
    }
  },
);

/**
 * Fetch a shipment by token with offline support
 */
export const fetchShipment = createAsyncThunk<
  PublicShipmentForDrivers,
  { token: string; isProtected?: boolean },
  { rejectValue: string }
>("driver/fetchShipment", async ({ token, isProtected = false }, thunkAPI) => {
  try {
    const api = getDriverAPI(isProtected);
    const response = await api.getShipmentByID(token);
    const shipment = response.data.data.shipment;

    // Cache the shipment for offline access
    if (isProtected) {
      // Permanent driver - cache by ID
      const { cacheShipment } = await import("../../utils/indexedDB");
      await cacheShipment(token, shipment).catch((err) => {
        console.warn("[Offline] Failed to cache shipment:", err);
      });
    } else {
      // Temporary driver - cache by token
      const { cachePublicShipment } = await import("../../utils/indexedDB");
      await cachePublicShipment(token, shipment).catch((err) => {
        console.warn("[Offline] Failed to cache public shipment:", err);
      });
    }

    return shipment;
  } catch (err: any) {
    // If network fails, try to get from cache
    if (!navigator.onLine || err.code === "ERR_NETWORK") {
      // console.log("[Offline] Network failed, trying cache...");

      try {
        let cachedShipment;

        if (isProtected) {
          const { getCachedShipment } = await import("../../utils/indexedDB");
          cachedShipment = await getCachedShipment(token);
        } else {
          const { getCachedPublicShipment } =
            await import("../../utils/indexedDB");
          cachedShipment = await getCachedPublicShipment(token);
        }

        if (cachedShipment) {
          // console.log(
          //   "[Offline] Serving from cache:",
          //   cachedShipment.shipmentNumber,
          // );
          // Mark as from cache in metadata
          (cachedShipment as any)._isFromCache = true;
          return cachedShipment as PublicShipmentForDrivers;
        }
      } catch (cacheErr) {
        console.error("[Offline] Cache retrieval failed:", cacheErr);
      }
    }

    return thunkAPI.rejectWithValue(
      err.response?.data?.message || "Failed to load shipment",
    );
  }
});

/**
 * Set shipment as Ready for Pickup
 */
export const setReadyForPickup = createAsyncThunk<
  void,
  { token: string; latitude: number; longitude: number; isProtected?: boolean },
  { rejectValue: string }
>(
  "driver/setReadyForPickup",
  async ({ token, latitude, longitude, isProtected = false }, thunkAPI) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "STATUS_UPDATE",
          { token, status: "ReadyForPickup", latitude, longitude, isProtected },
          token,
        );
        return;
      }
      const api = getDriverAPI(isProtected);
      await api.setReadyForPickup(token, latitude, longitude);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to set ready for pickup",
      );
    }
  },
);

/**
 * Set shipment as awaiting pickup
 */
export const setAwaitingPickup = createAsyncThunk<
  void,
  { token: string; latitude: number; longitude: number; isProtected?: boolean },
  { rejectValue: string }
>(
  "driver/setAwaitingPickup",
  async ({ token, latitude, longitude, isProtected = false }, thunkAPI) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "STATUS_UPDATE",
          { token, status: "AwaitingPickup", latitude, longitude, isProtected },
          token,
        );
        return;
      }
      const api = getDriverAPI(isProtected);
      await api.setAwaitingPickup(token, latitude, longitude);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to set awaiting pickup",
      );
    }
  },
);

/**
 * Start journey
 */
export const startJourney = createAsyncThunk<
  void,
  { token: string; latitude: number; longitude: number; isProtected?: boolean },
  { rejectValue: string }
>(
  "driver/startJourney",
  async ({ token, latitude, longitude, isProtected = false }, thunkAPI) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "STATUS_UPDATE",
          { token, status: "InTransit", latitude, longitude, isProtected },
          token,
        );
        return;
      }
      const api = getDriverAPI(isProtected);
      await api.startJourney(token, latitude, longitude);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to start journey",
      );
    }
  },
);

/**
 * Complete journey
 */
export const completeJourney = createAsyncThunk<
  void,
  {
    token: string;
    latitude: number;
    longitude: number;
    isProtected?: boolean;
    otp?: string;
  },
  { rejectValue: string }
>(
  "driver/completeJourney",
  async (
    { token, latitude, longitude, isProtected = false, otp },
    thunkAPI,
  ) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "STATUS_UPDATE",
          { token, status: "Completed", latitude, longitude, isProtected, otp },
          token,
        );
        return;
      }
      const api = getDriverAPI(isProtected);
      await api.completeJourney(token, latitude, longitude, otp);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to complete journey",
      );
    }
  },
);

export const addService = createAsyncThunk<
  { services: ServiceSnapshot[] },
  { token: string; services: ServiceSnapshot[]; isProtected?: boolean },
  { rejectValue: string }
>(
  "driver/addService",
  async ({ token, services, isProtected = false }, thunkAPI) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "ADD_SERVICE",
          { token, services, isProtected },
          token,
        );
        return { services };
      }
      const api = getDriverAPI(isProtected);
      const response = await api.addService(token, services);
      return response.data.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to add services",
      );
    }
  },
);

/**
 * Upload attachments
 */
export const uploadDriverAttachments = createAsyncThunk<
  DriverAttachment[],
  { token: string; formData: FormData; isProtected?: boolean },
  { rejectValue: string }
>(
  "driver/uploadAttachments",
  async ({ token, formData, isProtected = false }, thunkAPI) => {
    try {
      if (!navigator.onLine) {
        const formDataEntries: Record<string, any> = {};
        formData.forEach((value, key) => {
          formDataEntries[key] = value;
        });
        await enqueueAction(
          "UPLOAD_ATTACHMENT",
          { token, formDataEntries, isProtected },
          token,
        );
        return thunkAPI.rejectWithValue(
          "Offline: attachment upload queued and will sync when online",
        );
      }
      const api = getDriverAPI(isProtected);
      const response = await api.uploadAttachments(token, formData);
      return response.data.data.attachments;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to upload attachments",
      );
    }
  },
);

/**
 * Delete attachment
 */
export const deleteDriverAttachment = createAsyncThunk<
  string,
  { token: string; attachmentId: string; isProtected?: boolean },
  { rejectValue: string }
>(
  "driver/deleteAttachment",
  async ({ token, attachmentId, isProtected = false }, thunkAPI) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "DELETE_ATTACHMENT",
          { token, attachmentId, isProtected },
          token,
        );
        return attachmentId;
      }
      const api = getDriverAPI(isProtected);
      await api.deleteAttachment(token, attachmentId);
      return attachmentId;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to delete attachment",
      );
    }
  },
);

/**
 * Download attachment
 */
export const downloadDriverAttachment = createAsyncThunk<
  void,
  {
    token: string;
    attachmentId: string;
    originalName: string;
    isProtected?: boolean;
  },
  { rejectValue: string }
>(
  "driver/downloadAttachment",
  async (
    { token, attachmentId, originalName, isProtected = false },
    thunkAPI,
  ) => {
    try {
      const api = getDriverAPI(isProtected);
      const response = await api.downloadAttachment(token, attachmentId);

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = originalName || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to download attachment",
      );
    }
  },
);

// Slice
const driverSlice = createSlice({
  name: "driver",
  initialState,
  reducers: {
    clearDriverState: (state) => {
      state.shipment = null;
      state.loading = false;
      state.error = null;
      state.isFromCache = false;
      state.cachedAt = null;
    },
    clearError: (state) => {
      state.error = null;
      state.attachmentError = null;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
    },
    resetDriverState: () => initialState,
  },
  extraReducers: (builder) => {
    // Get Shipments
    builder
      .addCase(fetchShipments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShipments.fulfilled, (state, action) => {
        state.loading = false;
        state.shipments = action.payload.shipments;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchShipments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      // Fetch Driver Groups
      .addCase(fetchDriverGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDriverGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.groups;
        state.error = null;
      })
      .addCase(fetchDriverGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Driver Group By ID
      .addCase(fetchDriverGroupById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDriverGroupById.fulfilled,
        (state, action: PayloadAction<ShipmentGroupForDrivers>) => {
          state.loading = false;
          state.currentGroup = action.payload;
          state.error = null;
        },
      )
      .addCase(fetchDriverGroupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(fetchShipment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isFromCache = false;
        state.cachedAt = null;
      })
      .addCase(
        fetchShipment.fulfilled,
        (state, action: PayloadAction<PublicShipmentForDrivers>) => {
          state.shipment = action.payload;
          state.loading = false;

          // Check if this is from cache
          const cachedData = action.payload as any;
          if (cachedData._isFromCache) {
            state.isFromCache = true;
            state.cachedAt = cachedData._cached_at || null;
          }
        },
      )
      .addCase(fetchShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Error loading shipment";
      });

    // handle awaiting pickup
    builder.addCase(setReadyForPickup.fulfilled, (state) => {
      if (state.shipment) {
        state.shipment.status = "ReadyForPickup";
      }
      state.loading = false;
    });

    builder.addCase(setAwaitingPickup.fulfilled, (state) => {
      if (state.shipment) {
        state.shipment.status = "AwaitingPickup";
      }
      state.loading = false;
    });

    // handle start journey
    builder.addCase(startJourney.fulfilled, (state) => {
      if (state.shipment) {
        state.shipment.status = "InTransit";
        if (state.shipment.driverAccess) {
          state.shipment.driverAccess.journeyStarted = true;
        }
        if (!state.shipment.driverAccess.journeyStartTime) {
          state.shipment.driverAccess.journeyStartTime =
            new Date().toISOString();
        }
      }
      state.loading = false;
    });

    // handle complete journey
    builder.addCase(completeJourney.fulfilled, (state) => {
      if (state.shipment) {
        state.shipment.status = "Completed";
        if (state.shipment.driverAccess) {
          state.shipment.driverAccess.locked = true;
          state.shipment.driverAccess.journeyCompleted = true;
        }
        if (!state.shipment.driverAccess.journeyCompletedTime) {
          state.shipment.driverAccess.journeyCompletedTime =
            new Date().toISOString();
        }
      }
      state.loading = false;
    });

    // handle rejected and pending states for actions
    [
      setReadyForPickup,
      setAwaitingPickup,
      startJourney,
      completeJourney,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload ?? "Driver action failed";
        });
    });

    // Upload attachments
    builder
      .addCase(uploadDriverAttachments.pending, (state) => {
        state.attachmentLoading = true;
        state.attachmentError = null;
      })
      .addCase(uploadDriverAttachments.fulfilled, (state, action) => {
        if (state.shipment) {
          state.shipment.driverAttachments = [
            ...(state.shipment.driverAttachments || []),
            ...action.payload,
          ];
        }
        state.attachmentLoading = false;
      })
      .addCase(uploadDriverAttachments.rejected, (state, action) => {
        state.attachmentLoading = false;
        state.attachmentError = action.payload ?? "Attachment upload failed";
      });

    // Delete attachments
    builder
      .addCase(deleteDriverAttachment.pending, (state) => {
        state.attachmentLoading = true;
        state.attachmentError = null;
      })
      .addCase(deleteDriverAttachment.fulfilled, (state, action) => {
        if (state.shipment?.driverAttachments) {
          state.shipment.driverAttachments =
            state.shipment.driverAttachments.filter(
              (att) => att._id !== action.payload,
            );
        }
        state.attachmentLoading = false;
      })
      .addCase(deleteDriverAttachment.rejected, (state, action) => {
        state.attachmentLoading = false;
        state.attachmentError = action.payload ?? "Attachment delete failed";
      });
    // Download Attachments
    builder
      .addCase(downloadDriverAttachment.pending, (state) => {
        state.attachmentLoading = true;
        state.attachmentError = null;
      })
      .addCase(downloadDriverAttachment.fulfilled, (state) => {
        state.attachmentLoading = false;
      })
      .addCase(downloadDriverAttachment.rejected, (state, action) => {
        state.attachmentLoading = false;
        state.attachmentError = action.payload ?? "Download failed";
      });
    // Add services
    builder
      .addCase(addService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addService.fulfilled, (state, action) => {
        if (state.shipment) {
          state.shipment.services = action.payload.services;
        }
        state.loading = false;
      })
      .addCase(addService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to add services";
      });
  },
});

export const { clearDriverState, clearError, clearCurrentGroup } =
  driverSlice.actions;
export default driverSlice.reducer;

// Selectors
export const selectIsFromCache = (state: { driver: DriverState }) =>
  state.driver.isFromCache;
export const selectCachedAt = (state: { driver: DriverState }) =>
  state.driver.cachedAt;
